import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
  Collapse
} from '@mui/material'
import {
  Dashboard,
  Description,
  Business,
  People,
  Search,
  Settings,
  Add,
  ExpandLess,
  ExpandMore,
  Folder,
  AccountTree,
  Group,
  Assignment,
  MenuBook,
  Gavel,
  LibraryBooks,
  Security,
  Tune,
  Info,
  SmartToy,
  Build,
  Delete
} from '@mui/icons-material'
import { useAuth } from '../../contexts/AuthContext'
import { useSystemLogo } from '../../hooks/useSystemConfig'

const drawerWidth = 280
const drawerCollapsedWidth = 72

const Sidebar = ({ open, onClose, isMobile }) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, hasPermission } = useAuth()
  const { logoUrl, loading: logoLoading } = useSystemLogo()
  const [expandedItems, setExpandedItems] = React.useState({})

  const handleItemClick = (path) => {
    navigate(path)
    if (isMobile) {
      onClose()
    }
  }

  const handleExpandClick = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }))
  }

  const isActive = (path) => location.pathname === path

  const menuItems = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: <Dashboard />,
      path: '/dashboard',
      roles: ['administrador', 'editor', 'consultor']
    },
    {
      id: 'procedimientos',
      title: 'Procedimientos',
      icon: <Description />,
      roles: ['administrador', 'editor', 'consultor'],
      expandable: true,
      children: [
        {
          id: 'ver-procedimientos',
          title: 'Ver Procedimientos',
          path: '/procedimientos',
          roles: ['administrador', 'editor', 'consultor']
        },
        {
          id: 'nuevo-procedimiento',
          title: 'Crear Procedimiento',
          path: '/procedimientos/nuevo',
          icon: <Add />,
          roles: ['administrador', 'editor']
        },
        {
          id: 'procedimientos-eliminados',
          title: 'Papelera',
          path: '/procedimientos/eliminados',
          icon: <Delete />,
          roles: ['administrador']
        }
      ]
    },
    {
      id: 'procesos',
      title: 'Procesos',
      icon: <Assignment />,
      path: '/procesos',
      roles: ['administrador', 'editor', 'consultor']
    },
    {
      id: 'gestiones',
      title: 'Gestiones',
      icon: <Business />,
      path: '/gestiones',
      roles: ['administrador', 'editor', 'consultor']
    },
    {
      id: 'auditoria',
      title: 'Auditoría',
      icon: <Security />,
      path: '/auditoria',
      roles: ['administrador', 'rector']
    },
    {
      id: 'buscar',
      title: 'Búsqueda',
      icon: <Search />,
      path: '/buscar',
      roles: ['administrador', 'editor', 'consultor']
    },
    {
      id: 'herramientas',
      title: 'Herramientas',
      icon: <Build />,
      path: '/herramientas',
      roles: ['administrador', 'editor', 'consultor']
    },
    {
      id: 'acerca-de',
      title: 'Acerca de',
      icon: <Info />,
      path: '/acerca-de',
      roles: ['administrador', 'editor', 'consultor']
    }
  ]

  const adminItems = [
    {
      id: 'configuracion',
      title: 'Configuración',
      icon: <Settings />,
      roles: ['administrador'],
      expandable: true,
      children: [
        {
          id: 'configuracion-sistema',
          title: 'Sistema',
          icon: <Tune />,
          path: '/configuracion/sistema',
          roles: ['administrador']
        },
        {
          id: 'usuarios',
          title: 'Usuarios',
          icon: <People />,
          path: '/configuracion/usuarios',
          roles: ['administrador']
        },
        {
          id: 'responsables',
          title: 'Responsables',
          icon: <Group />,
          path: '/configuracion/responsables',
          roles: ['administrador']
        },
        {
          id: 'definiciones',
          title: 'Definiciones',
          icon: <MenuBook />,
          path: '/configuracion/definiciones',
          roles: ['administrador']
        },
        {
          id: 'normatividad',
          title: 'Normatividad',
          icon: <Gavel />,
          path: '/configuracion/normatividad',
          roles: ['administrador']
        },
        {
          id: 'conocimiento',
          title: 'Base de Conocimiento',
          icon: <LibraryBooks />,
          path: '/configuracion/conocimiento',
          roles: ['administrador']
        },
        {
          id: 'contextos-ia',
          title: 'Contextos de IA',
          icon: <SmartToy />,
          path: '/configuracion/contextos-ia',
          roles: ['administrador']
        },
        {
          id: 'logs',
          title: 'Logs de Auditoría',
          icon: <Security />,
          path: '/configuracion/logs',
          roles: ['administrador']
        }
      ]
    }
  ]

  const renderMenuItem = (item, level = 0) => {
    if (!hasPermission(item.roles)) {
      return null
    }

    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedItems[item.id]

    return (
      <React.Fragment key={item.id}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            selected={item.path ? isActive(item.path) : false}
            onClick={() => {
              if (hasChildren) {
                handleExpandClick(item.id)
              } else if (item.path) {
                handleItemClick(item.path)
              }
            }}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: open ? 2.5 : 1,
              pl: level > 0 ? (open ? 4 : 1) : (open ? 2.5 : 1),
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 0,
                justifyContent: 'center',
                width: open ? 'auto' : '100%',
              }}
            >
              {item.icon || <Folder />}
            </ListItemIcon>
            <ListItemText 
              primary={item.title} 
              sx={{ opacity: open ? 1 : 0 }}
            />
            {hasChildren && open && (
              isExpanded ? <ExpandLess /> : <ExpandMore />
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded && open} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => renderMenuItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    )
  }

  const drawerContent = (
    <Box sx={{ overflow: 'auto' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          minHeight: 64,
          bgcolor: 'primary.dark',
          color: 'white',
          position: 'relative',
          zIndex: 1201
        }}
      >
        <AccountTree />
      </Box>

      <List>
        {menuItems.map((item) => renderMenuItem(item))}
      </List>

      {/* Logo ENSAS - Visible para todos los usuarios */}
      {open && (
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            p: 2,
            mt: 1,
            mb: 1
          }}
        >
          {!logoLoading && (
            <img 
              src={logoUrl} 
              alt="Escuela Normal Superior del Alto Sinú" 
              style={{
                width: 'calc(100% - 32px)', // Casi todo el ancho menos padding
                maxWidth: '220px', // Máximo para sidebars muy anchos
                height: 'auto',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transition: 'transform 0.2s ease',
              }}
              onError={(e) => {
                // Si falla cargar la imagen, usar el logo por defecto
                e.target.src = '/ensas_logo.png'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'scale(1.02)'
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)'
              }}
            />
          )}
        </Box>
      )}

      {hasPermission(['administrador']) && (
        <>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ px: 2, py: 1 }}>
            {open && (
              <Typography variant="overline" color="text.secondary">
                Administración
              </Typography>
            )}
          </Box>
          <List>
            {adminItems.map((item) => renderMenuItem(item))}
          </List>
        </>
      )}

      {open && (
        <Box sx={{ mt: 'auto', p: 2, pt: hasPermission(['administrador']) ? 1 : 2 }}>
          <Divider sx={{ mb: 2 }} />
          
          {user?.gestion && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Mi Gestión
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                <Folder sx={{ fontSize: 16, mr: 1, color: 'primary.main' }} />
                <Typography variant="body2">
                  {user.gestion.codigo}
                </Typography>
              </Box>
            </Box>
          )}

          <Typography variant="caption" color="text.secondary">
            Escuela Normal Superior del Alto Sinú
          </Typography>
        </Box>
      )}
    </Box>
  )

  return (
    <Drawer
      variant={isMobile ? 'temporary' : 'persistent'}
      anchor="left"
      open={open}
      onClose={onClose}
      sx={{
        width: open ? drawerWidth : drawerCollapsedWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : drawerCollapsedWidth,
          transition: (theme) => theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          zIndex: 1200,
        },
      }}
      ModalProps={{
        keepMounted: true,
      }}
    >
      {drawerContent}
    </Drawer>
  )
}

export default Sidebar